import { emptyDocument, newEntityId, type CadDocument, type Layer } from './model/document';
import { entityBox, type BlockDef, type CadEntity } from './model/entity';
import type { Box } from './model/geom';
import { invertBatch, type CadOp, type OpBatch } from './ops/operation';
import { SpatialGrid } from './spatial/grid';

/**
 * Scene — 편집 중인 도면의 작업 상태 (작업지시서 P2-01 / P2-06)
 *
 * 문서(CadDocument) + 조회 색인(Map) + 공간 색인(SpatialGrid) + 실행 취소 이력을
 * 한 덩어리로 묶는다. 명령·렌더러·React UI 가 전부 이 객체를 통해 도면을 본다.
 *
 * 원칙:
 *   · 도면 변경은 **반드시** applyBatch() 를 통과한다. 직접 배열을 만지지 않는다.
 *     그래야 공간 색인·실행 취소·자동 저장이 자동으로 따라온다.
 *   · React 는 이 객체를 상태로 두지 않는다(불변 복사 비용이 너무 크다).
 *     대신 버전 카운터를 구독해 다시 그린다.
 */

export type SceneListener = (scene: Scene, batch: OpBatch | null) => void;

export class Scene {
  doc: CadDocument;

  private byId = new Map<string, CadEntity>();
  private blockMap = new Map<string, BlockDef>();
  private grid = new SpatialGrid();

  private undoStack: OpBatch[] = [];
  private redoStack: OpBatch[] = [];

  /** 저장되지 않은 연산. 자동 저장이 비우고 간다. */
  private pending: OpBatch[] = [];

  private listeners = new Set<SceneListener>();

  /** 변경될 때마다 증가. React 가 이 값으로 리렌더를 판단한다. */
  version = 0;

  constructor(doc: CadDocument = emptyDocument()) {
    this.doc = doc;
    this.reindex();
  }

  /* ---------------------------------------------------------------- 조회 */

  get entities(): CadEntity[] {
    return this.doc.entities;
  }

  get count(): number {
    return this.byId.size;
  }

  get blocks(): Map<string, BlockDef> {
    return this.blockMap;
  }

  get(id: string): CadEntity | undefined {
    return this.byId.get(id);
  }

  layer(code: string): Layer | undefined {
    return this.doc.layers.find((l) => l.code === code);
  }

  /** 레이어가 보이는가 (숨김 레이어는 렌더·선택에서 제외) */
  isVisible(e: CadEntity): boolean {
    const l = this.layer(e.layer);
    return l ? l.visible : true;
  }

  /** 레이어가 잠겼는가 (잠긴 레이어는 선택·편집 불가) */
  isLocked(e: CadEntity): boolean {
    const l = this.layer(e.layer);
    return l ? l.locked : false;
  }

  /** 질의 상자와 겹칠 가능성이 있는 엔티티 (경계 상자 기준) */
  queryBox(box: Box): CadEntity[] {
    const out: CadEntity[] = [];
    for (const id of this.grid.query(box)) {
      const e = this.byId.get(id);
      if (e) out.push(e);
    }
    return out;
  }

  boxOf(id: string): Box | undefined {
    return this.grid.boxOf(id);
  }

  /** 전체 도면 범위. ZOOM Extents 용. */
  extent(): Box | null {
    return this.grid.extent();
  }

  /* -------------------------------------------------------------- 색인 */

  private reindex(): void {
    this.byId.clear();
    this.grid.clear();
    this.blockMap.clear();

    for (const b of this.doc.blocks) this.blockMap.set(b.code, b);
    for (const e of this.doc.entities) {
      this.byId.set(e.id, e);
      this.grid.insert(e.id, entityBox(e, this.blockMap));
    }
  }

  /* -------------------------------------------------------------- 변경 */

  /**
   * 연산 묶음을 적용한다. 편집으로 들어오는 유일한 통로.
   * @param record false 면 실행 취소 이력에 남기지 않는다(undo/redo 재생 시).
   */
  applyBatch(batch: OpBatch, record = true): void {
    for (const op of batch.ops) this.applyOp(op);

    if (record) {
      this.undoStack.push(batch);
      this.redoStack.length = 0; // 새 편집이 들어오면 redo 는 무효
      this.pending.push(batch);
      // 이력이 무한정 커지지 않도록 상한을 둔다. 넘친 것은 이미 저장된 상태다.
      if (this.undoStack.length > 500) this.undoStack.shift();
    }

    this.version++;
    this.emit(batch);
  }

  /** 명령이 만든 연산들을 하나의 실행취소 단위로 묶어 적용한다. */
  commit(command: string, ops: CadOp[]): OpBatch | null {
    if (ops.length === 0) return null;
    const batch: OpBatch = {
      id: newEntityId(),
      command,
      ops,
      at: Date.now(),
    };
    this.applyBatch(batch);
    return batch;
  }

  private applyOp(op: CadOp): void {
    switch (op.kind) {
      case 'add':
        for (const e of op.entities) this.insertEntity(e);
        break;

      case 'delete':
        for (const e of op.entities) this.removeEntity(e.id);
        break;

      case 'update':
        for (const e of op.after) this.replaceEntity(e);
        break;

      case 'layer':
        this.doc.layers = op.after.map((l) => ({ ...l }));
        break;

      case 'meta':
        Object.assign(this.doc, op.after);
        if (op.after.blocks) this.reindex();
        break;
    }
  }

  private insertEntity(e: CadEntity): void {
    if (this.byId.has(e.id)) {
      this.replaceEntity(e);
      return;
    }
    this.byId.set(e.id, e);
    this.doc.entities.push(e);
    this.grid.insert(e.id, entityBox(e, this.blockMap));
  }

  private removeEntity(id: string): void {
    if (!this.byId.delete(id)) return;
    const i = this.doc.entities.findIndex((x) => x.id === id);
    if (i >= 0) this.doc.entities.splice(i, 1);
    this.grid.remove(id);
  }

  private replaceEntity(e: CadEntity): void {
    const i = this.doc.entities.findIndex((x) => x.id === e.id);
    if (i >= 0) {
      this.doc.entities[i] = e;
    } else {
      this.doc.entities.push(e);
    }
    this.byId.set(e.id, e);
    this.grid.update(e.id, entityBox(e, this.blockMap));
  }

  /* ------------------------------------------------------- 실행 취소 */

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /** 되돌린 명령 이름. 없으면 null. */
  undo(): string | null {
    const batch = this.undoStack.pop();
    if (!batch) return null;

    const inverse = invertBatch(batch);
    this.applyBatch(inverse, false);
    this.redoStack.push(batch);
    this.pending.push(inverse); // 되돌림도 하나의 연산으로 기록한다
    return batch.command;
  }

  redo(): string | null {
    const batch = this.redoStack.pop();
    if (!batch) return null;

    this.applyBatch(batch, false);
    this.undoStack.push(batch);
    this.pending.push(batch);
    return batch.command;
  }

  /** 실행 취소 이력 (F2 명령 히스토리 패널) */
  history(): OpBatch[] {
    return [...this.undoStack];
  }

  /* ------------------------------------------------------- 자동 저장 */

  get isDirty(): boolean {
    return this.pending.length > 0;
  }

  /** 아직 저장되지 않은 연산을 꺼내 간다. 저장 실패 시 restorePending() 으로 되돌린다. */
  takePending(): OpBatch[] {
    const out = this.pending;
    this.pending = [];
    return out;
  }

  /** 저장 실패 시 되돌린다. 유실 0 을 보장하기 위한 경로. */
  restorePending(batches: OpBatch[]): void {
    this.pending = [...batches, ...this.pending];
  }

  /* -------------------------------------------------------------- 구독 */

  subscribe(fn: SceneListener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(batch: OpBatch | null): void {
    for (const fn of this.listeners) fn(this, batch);
  }

  /** 외부에서 문서를 통째로 교체 (버전 열기 · 되감기) */
  load(doc: CadDocument): void {
    this.doc = doc;
    this.reindex();
    this.undoStack.length = 0;
    this.redoStack.length = 0;
    this.pending.length = 0;
    this.version++;
    this.emit(null);
  }
}
